import React, { useState } from "react";
import { generalFilter, useDefaultColumn } from "../../../../../util/filter";
import { useFilters, useGlobalFilter, useSortBy, useTable } from "react-table";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import CallSplitIcon from "@material-ui/icons/CallSplit";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import MuiTable from "@material-ui/core/Table";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { useHistory } from "react-router-dom";
import { formatDate } from "../../../../common/formatDate";
import { useDocumentTempDelete } from "../../../projectManagementQueries";
import { useFetchContext } from "../../../../common/FetchContext";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

export const DocumentTemplateTable = ({ data, refetch }: any) => {
  const defaultColumn = useDefaultColumn();
  const history = useHistory();
  // const { mutateAsync: UploadTemplateDelete } =
  //   useDocumentTempDelete();
  const { authAxios } = useFetchContext();
  const queryClient = useQueryClient();
  const { mutateAsync: deleteTemplate, isLoading: isDeleting } =
    useDocumentTempDelete();

  // State to manage the custom confirmation dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);

  // Opens the dialog and stores the ID of the row to delete
  const handleOpenDialog = (template: any) => {
    setSelectedTemplate(template);
    setOpenDialog(true);
  };

  // Closes the dialog and resets the selected ID
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTemplate(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTemplate?.id) return;

    try {
      await deleteTemplate({
        authAxios,
        template_id: selectedTemplate.id,
      });

      await refetch();
      toast.success("Template is Deleted Successfully");

      handleCloseDialog();
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete the template");
    }
  };

  const columns: any = React.useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        filter: generalFilter,
      },
      {
        id: "Name",
        accessor: "name",
        filter: generalFilter,
        width: 300,
        minWidth: 250,
        maxWidth: 400,
        align: "center",
        Cell: ({ value }: any) => <>{value ? value : "N/A"}</>,
      },
      {
        id: "Report TypeName",
        accessor: "report_type_name",
        filter: generalFilter,
        width: 220,
        minWidth: 180,
        maxWidth: 300,
        align: "center",
        Cell: ({ value }: any) => <>{value ? value : "N/A"}</>,
      },
      {
        id: "Version",
        accessor: "version",
        filter: generalFilter,
        width: 120,
        minWidth: 100,
        maxWidth: 150,
        align: "center",
        Cell: ({ value }: any) => <>{value ? value : "N/A"}</>,
      },
      {
        id: "Description",
        accessor: "description",
        filter: generalFilter,
        width: 220,
        minWidth: 180,
        maxWidth: 300,
        align: "center",
        Cell: ({ value }: any) => <>{value ? value : "N/A"}</>,
      },
      {
        id: "Created Date",
        accessor: "created_at",
        filter: generalFilter,
        width: 150,
        minWidth: 120,
        maxWidth: 180,
        align: "center",
        Cell: ({ value }: any) => <>{value ? formatDate(value) : "N/A"}</>,
      },
      {
        id: "Edit",
        Header: "Edit",
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        disableFilters: true,
        Cell: ({ row }: any) => (
          <IconButton
            size="small"
            aria-label="edit"
            onClick={() => {
              history.push(
                `/reports/report_document_upload/report_preview_editor/${row.original.id}`,
              );
            }}
          >
            <EditIcon color="primary" />
          </IconButton>
        ),
      },

      // Delete column
      {
        id: "Delete",
        Header: "Delete",
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        disableFilters: true,
        Cell: ({ row }: any) => (
          <IconButton
            size="small"
            aria-label="delete"
            disabled={isDeleting}
            onClick={() => handleOpenDialog(row.original)}
          >
            <DeleteIcon style={{ color: "red" }} />
          </IconButton>
        ),
      },
    ],
    [],
  );

  const { getTableProps, visibleColumns, headerGroups, rows, prepareRow } =
    useTable(
      {
        data: data,
        columns,
        defaultColumn,
        initialState: {
          hiddenColumns: ["id"],
        },
      },
      useFilters,
      useGlobalFilter,
      useSortBy,
    );

  return (
    <>
      <TableContainer
        data-testid="tableContainer"
        // style={theme.tableContainer}
        component={Paper}
      >
        <MuiTable stickyHeader {...getTableProps()} size="small">
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <TableCell
                    style={{
                      // ...theme.noLeftRightPadding,
                      width: column.width,
                      minWidth: column.minWidth,
                      maxWidth: column.maxWidth,
                      textAlign: "center",
                    }}
                  >
                    <Grid
                      container
                      direction="row"
                      alignItems="center"
                      spacing={0}
                    >
                      <Grid item xs={12} {...column.getHeaderProps()}>
                        <Grid
                          container
                          direction="row"
                          alignItems="center"
                          spacing={0}
                        >
                          <Typography variant="subtitle2">
                            {column.render("Header")}
                          </Typography>
                          {column.canFilter ? column.render("Filter") : null}
                          <Grid item xs={2}>
                            <IconButton
                              type="submit"
                              aria-label="search"
                              size="small"
                              {...column.getSortByToggleProps()}
                            >
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <ArrowDropDownIcon />
                                ) : (
                                  <ArrowDropUpIcon />
                                )
                              ) : (
                                column.id !== "notes" &&
                                column.id !== "Edit" &&
                                column.id !== "Delete" && <CallSplitIcon />
                              )}
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <>
                  <TableRow {...row.getRowProps()}>
                    {row.cells.map((cell) => {
                      return (
                        <TableCell
                          {...cell.getCellProps()}
                          style={{
                            width: cell.column.width,
                            minWidth: cell.column.minWidth,
                            maxWidth: cell.column.maxWidth,
                            // textAlign: 'center',
                          }}
                        >
                          {cell.render("Cell")}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </>
              );
            })}
          </TableBody>
        </MuiTable>
      </TableContainer>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this template?
          </DialogContentText>

          <Typography variant="body1" style={{ marginTop: 16 }}>
            <strong>Template Name:</strong> {selectedTemplate?.name || "N/A"}
          </Typography>

          <Typography variant="body1" style={{ marginTop: 8 }}>
            <strong>Report Type:</strong>{" "}
            {selectedTemplate?.report_type_name || "N/A"}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            color="primary"
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="secondary"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
